import { useLazyQuery } from '@apollo/client';
import React, { FC, useEffect, useState, useContext } from 'react';
import { ForemanExpanded } from './foreman-expanded';
import { ReplanExpiredMilestoneStyled, ForemanHeaderStyled } from './styled';
import {
  Box,
  Text,
  Button,
  ButtonSize,
  ButtonAppearance,
  Icon,
  Spinner,
  SpinnerSize,
  useToastsDispatch,
  ToastAction,
  ToastType,
  IconSize,
  useMediaQuery,
  belowBreakpoint,
  Theme,
} from '@bechtel/gds';
import { PlanningCrewMemberInput, CrewMemberExpiredMilestoneStats } from '@bechtel/types';
import { EXPIRED_MILESTONES_COUNT } from '@bechtel/client-gql';
import { get } from 'lodash';
import { usePlanningPermissions } from '../../hooks';
import {
  AllGeneralForemanId,
  UnassignedGeneralForemanId,
  AllForemanId,
  UnassignedForemanId,
  AllSuperintendentId,
  UnassignedSuperintendentId,
  usePlanning,
  PlanningActionType,
} from '@bechtel/client-common';
import { PlanningTab } from '../../types';
import { ForemanCollapsed } from './foreman-collapsed';
import { ThemeContext } from 'styled-components';
import { ForemanSelectorProps, ReplanExpiredMilestoneProps } from '../types';
import { Plural, Trans } from '@lingui/macro';

const ReplanExpiredMilestone: FC<ReplanExpiredMilestoneProps> = ({ hidden, expiredMilestoneStats }) => {
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const { selectedForeman, selectedGeneralForeman, selectedSuperintendent, planningDispatch, isReplan } = usePlanning();
  const { canPlanMilestones } = usePlanningPermissions();
  const isAllOrUnassignedSup =
    selectedSuperintendent?.id === AllSuperintendentId || selectedSuperintendent?.id === UnassignedSuperintendentId;
  const [getExpiredMilestoneCount, { loading, data, error }] = useLazyQuery<
    { expiredMilestoneCount: CrewMemberExpiredMilestoneStats },
    {
      input: PlanningCrewMemberInput;
    }
  >(EXPIRED_MILESTONES_COUNT, { fetchPolicy: 'network-only', nextFetchPolicy: 'cache-first' }); //Always get the latest server count

  useEffect(() => {
    let filterPersona;
    if (
      selectedGeneralForeman?.id === UnassignedGeneralForemanId ||
      selectedGeneralForeman?.id === AllGeneralForemanId
    ) {
      filterPersona = selectedSuperintendent;
    } else if (selectedForeman?.id === UnassignedForemanId || selectedForeman?.id === AllForemanId) {
      filterPersona = selectedGeneralForeman;
    }
    filterPersona = filterPersona ? filterPersona : selectedForeman;
    if (!isAllOrUnassignedSup)
      getExpiredMilestoneCount({
        variables: {
          input: {
            persona: filterPersona?.persona,
            crewMemberId: filterPersona?.id,
          },
        },
      });
  }, [selectedSuperintendent, selectedGeneralForeman, selectedForeman, getExpiredMilestoneCount, isAllOrUnassignedSup]);

  useEffect(() => {
    if (expiredMilestoneStats)
      expiredMilestoneStats.current = get(data, 'expiredMilestoneCount', { id: '0', numExpiredMilestones: 0 });
  }, [data, expiredMilestoneStats]);

  const numExpiredMilestones = get(data?.expiredMilestoneCount, 'numExpiredMilestones', 0);

  const dispatchToast = useToastsDispatch();

  const ReplanToast = () => {
    return (
      <Plural
        value={numExpiredMilestones}
        one={`${numExpiredMilestones} expired milestone replanned`}
        other={`${numExpiredMilestones} expired milestones replanned`}
      />
    );
  };

  const replanMilestonesToast: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      toastType: ToastType.SUCCESS,
      content: ReplanToast,
      autoDismissTimeout: 5000,
    },
  };

  return (
    <Box px={{ sm: '500' }} mb={{ sm: '550' }} hidden={hidden}>
      <ReplanExpiredMilestoneStyled px={{ sm: isDesktop ? '450' : '500' }} isAllOrUnassignedSup={isAllOrUnassignedSup}>
        <Box mr={{ sm: '300' }} css={{ 'min-width': '16px' }}>
          {loading && <Spinner size={SpinnerSize.SMALL} />}
          {!loading && !error && (
            <Text size={isDesktop ? '400' : '600'} family="graphik" weight="600" color="grey.800">
              {isAllOrUnassignedSup ? '-' : numExpiredMilestones ? numExpiredMilestones : 0}
            </Text>
          )}
        </Box>
        <Text size="200" weight="600" color="grey.800">
          <>
            <Trans>Expired</Trans> {numExpiredMilestones > 1 ? <Trans>Milestones</Trans> : <Trans>Milestone</Trans>}
          </>
        </Text>
      </ReplanExpiredMilestoneStyled>
      {canPlanMilestones && !isReplan && (
        <Button
          disabled={
            selectedForeman?.id === AllForemanId ||
            selectedForeman?.id === UnassignedForemanId ||
            numExpiredMilestones < 1
          }
          onClick={() => {
            planningDispatch({ type: PlanningActionType.ReplanMilestone, payload: { isReplan: true } });
            dispatchToast(replanMilestonesToast);
          }}
          css={`
            ${({ theme }) => `background-color: ${theme.colors.blue[500]};`}
          `}
          size={ButtonSize.SMALL}
          appearance={ButtonAppearance.PRIMARY}
          data-testid="replan-milestones-button"
        >
          <Box mr={{ sm: '400' }}>
            <Icon fillColor="white" icon="redo" size={isDesktop ? IconSize.SMALL : IconSize.MEDIUM} />
          </Box>
          <Text size={isDesktop ? '200' : '300'} color="white">
            <Trans>Replan</Trans>
          </Text>
        </Button>
      )}
    </Box>
  );
};

export const ForemanSelector: FC<ForemanSelectorProps> = ({ expiredMilestoneStats, selectedTab }) => {
  const [foremanExpanded, setForemanExpanded] = useState(true);

  return (
    <Box flexDirection={{ sm: 'column' }}>
      <ForemanHeaderStyled
        onClick={() => {
          setForemanExpanded(!foremanExpanded);
        }}
      >
        <Text size="400" weight="600" family="graphik">
          <Trans>Select a Foreman</Trans>
        </Text>
        <Icon
          size={IconSize.MEDIUM}
          iconStyle="duo"
          fillColor="grey.1000"
          icon={foremanExpanded ? 'chevron-down' : 'chevron-up'}
        />
      </ForemanHeaderStyled>
      <ForemanCollapsed hidden={foremanExpanded} />
      <ForemanExpanded hidden={!foremanExpanded} />
      {selectedTab == PlanningTab.PLAN && (
        <ReplanExpiredMilestone hidden={!foremanExpanded} expiredMilestoneStats={expiredMilestoneStats} />
      )}
    </Box>
  );
};
